import {
	defaultTheme,
	Provider as V3Provider,
	View,
} from '@adobe/react-spectrum';
import { Provider as S2Provider } from '@react-spectrum/s2';
import { color, lightDark } from '@react-spectrum/s2/style' with {
	type: 'macro',
};

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Outlet, useHref, useNavigate, useNavigation } from 'react-router';

import { PageSpinner } from './components/PageSpinner';
import { BREAKPOINTS } from './hooks/useMatchingBreakpoints';

const queryClient = new QueryClient();

if (process.env.NODE_ENV !== 'production') {
	window.__TANSTACK_QUERY_CLIENT__ = queryClient;
	window.ENABLE_MOCKS = true;
}

// why would they return a string with [ and ]? lol
const skeletonHighlightColor = lightDark('gray-25', 'gray-300').replace(
	/\[|\]/g,
	'',
);

function useAbsoluteHref(path: string) {
	const relative = useHref(path);
	if (
		path.startsWith('https://') ||
		path.startsWith('http://') ||
		path.startsWith('mailto:')
	) {
		return path;
	}
	return relative;
}

export function App() {
	const navigate = useNavigate();
	const navigation = useNavigation();
	const isNavigating = Boolean(navigation.location);

	return (
		<QueryClientProvider client={queryClient}>
			<V3Provider
				theme={defaultTheme}
				router={{ navigate, useHref: useAbsoluteHref }}
				breakpoints={BREAKPOINTS}
				colorScheme="light"
			>
				<S2Provider
					background="base"
					router={{ navigate, useHref: useAbsoluteHref }}
					// colorScheme="light" — set on the html element
				>
					<NuqsAdapter>
						<SkeletonTheme
							// imitate s2's skeleton which we can't import rn
							baseColor={color('gray-100')}
							highlightColor={skeletonHighlightColor}
							customHighlightBackground="linear-gradient(to right, var(--base-color) 33%, var(--highlight-color), var(--base-color) 66%)"
							enableAnimation
							height={12}
						>
							<View
								maxWidth="100vw"
								minHeight="100vh"
								UNSAFE_style={{ backgroundColor: color('gray-50') }}
								paddingTop="24px"
								paddingBottom="24px"
							>
								{isNavigating && <PageSpinner />}
								<Outlet />
							</View>
						</SkeletonTheme>
					</NuqsAdapter>
				</S2Provider>
			</V3Provider>
		</QueryClientProvider>
	);
}
